import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Grid,
  Button,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Rating,
  TextareaAutosize,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  VideoLibrary as VideoLibraryIcon,
  Checklist as ChecklistIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { useChecklist } from '../contexts/ChecklistContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/config';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from '../components/Sidebar';
import { getModuleDescription, getDefaultDescription } from '../data/moduleDescriptions';
import { getModuleChecklist, getDefaultChecklist } from '../data/moduleDescriptions';
import { getMicrolearningVideo } from '../data/microlearningVideos';

const Checklist = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checklistItems, toggleItem, isLoaded, quizResult, saveChecklistProgressWithMarkedAsDone } = useChecklist();
  
  // Get milieu name from quiz result
  const milieuName = quizResult?.primary?.name || null;
  
  // Function to get the description for an item based on milieu
  const getItemDescription = (item) => {
    if (milieuName && item.moduleName) {
      const milieuDescription = getModuleDescription(item.moduleName, milieuName);
      if (milieuDescription) {
        return milieuDescription;
      }
    }
    // Fallback to default description from checklistData
    return item.description;
  };

  // Mapping von Milieu-Namen zu Abkürzungen
  const getMilieuAbbreviation = (milieuName) => {
    const mapping = {
      'Prekär': 'pre',
      'Konsum‑Hedonistisch': 'kh',
      'Traditionell': 'tra',
      'Konservativ‑Gehoben': 'kg',
      'Neo‑Ökologisch': 'öko',
      'Adaptiv‑Pragmatische Mitte': 'ap',
      'Performer': 'ap', // Fallback zu ap
      'Expeditiv': 'ap', // Fallback zu ap
      'Postmateriell': 'öko', // Fallback zu öko
      'Nostalgisch‑Bürgerlich': 'tra', // Fallback zu tra
    };
    return mapping[milieuName] || 'ap'; // Default zu ap
  };


  // Function to get the video path for an item based on milieu
  const getItemVideo = (item) => {
    if (!milieuName || !item.moduleName) {
      return null;
    }
    
    const milieuAbbr = getMilieuAbbreviation(milieuName);
    return getMicrolearningVideo(item.moduleName, milieuAbbr);
  };

  const handleMicrolearningToggle = (itemId) => {
    setExpandedMicrolearnings(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedVideos, setExpandedVideos] = useState({});
  const [expandedChecklists, setExpandedChecklists] = useState({});
  const [expandedMicrolearnings, setExpandedMicrolearnings] = useState({});
  const [markedAsDone, setMarkedAsDone] = useState({}); // itemId -> boolean
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingItemId, setPendingItemId] = useState(null);
  const [videoStats, setVideoStats] = useState({}); // { "moduleName-itemId-videoType": { views, rating, ... } }
  const [userFeedback, setUserFeedback] = useState({}); // { "moduleName-itemId-videoType": { rating, comment } }
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackVideoKey, setFeedbackVideoKey] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackCommentError, setFeedbackCommentError] = useState('');

  // Sanitize and validate comment input
  const sanitizeComment = (text) => {
    if (!text) return '';
    
    // Remove HTML tags
    let sanitized = text.replace(/<[^>]*>/g, '');
    
    // Remove potentially dangerous characters (script tags, event handlers, etc.)
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    return sanitized;
  };

  // Validate comment length and content
  const validateComment = (text) => {
    const MAX_LENGTH = 1000;
    
    if (text.length > MAX_LENGTH) {
      return `Kommentar darf maximal ${MAX_LENGTH} Zeichen lang sein.`;
    }
    
    // Check for suspicious patterns
    const suspiciousPatterns = [
      /<script/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /data:text\/html/gi,
    ];
    
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(text)) {
        return 'Kommentar enthält nicht erlaubte Inhalte.';
      }
    }
    
    return '';
  };
  const [quickFeedback, setQuickFeedback] = useState({}); // { "moduleName-itemId-videoType": "thumbs_up" | "thumbs_down" }

  useEffect(() => {
    // Nur Basissicherheit anzeigen - alle Module automatisch expandieren
    const basissicherheitCategory = checklistItems.find(cat => cat.category === 'Basissicherheit');
    if (basissicherheitCategory) {
      setExpandedCategories({ 'Basissicherheit': true });
    }
    
    // Load markedAsDone from database (via progress_data) or localStorage fallback
    const loadMarkedAsDone = async () => {
      if (user) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await fetch(`${API_CONFIG.BASE_URL}/checklist-progress/get`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const dbProgress = await response.json();
              if (dbProgress && dbProgress.progress_data) {
                try {
                  const parsed = JSON.parse(dbProgress.progress_data);
                  if (parsed.markedAsDone) {
                    setMarkedAsDone(parsed.markedAsDone);
                    // Also save to localStorage as fallback
                    const markedKey = `marked_as_done_${user.id}`;
                    localStorage.setItem(markedKey, JSON.stringify(parsed.markedAsDone));
                    return;
                  }
                } catch (e) {
                  console.error('Error parsing marked as done from database:', e);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading marked as done from database:', error);
        }
        
        // Fallback to localStorage
        const markedKey = `marked_as_done_${user.id}`;
        const saved = localStorage.getItem(markedKey);
        if (saved) {
          try {
            setMarkedAsDone(JSON.parse(saved));
          } catch (e) {
            console.error('Error parsing marked as done:', e);
          }
        }
      }
    };
    
    loadMarkedAsDone();
  }, [checklistItems, user]);

  // Load video statistics and user feedback
  useEffect(() => {
    const loadVideoData = async () => {
      if (!user || !checklistItems || checklistItems.length === 0) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      const basissicherheitCategory = checklistItems.find(cat => cat.category === 'Basissicherheit');
      if (!basissicherheitCategory) return;

      const statsPromises = [];
      const feedbackPromises = [];

      basissicherheitCategory.items.forEach(item => {
        // Load stats for microlearning videos
        if (getItemVideo(item)) {
          statsPromises.push(
            fetch(`${API_CONFIG.BASE_URL}/video-analytics/statistics/${encodeURIComponent(item.moduleName)}/${item.id}/microlearning`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).then(res => res.ok ? res.json() : null).catch(() => null)
          );
          feedbackPromises.push(
            fetch(`${API_CONFIG.BASE_URL}/video-analytics/feedback/${encodeURIComponent(item.moduleName)}/${item.id}/microlearning`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).then(res => {
              if (res.ok && res.status === 204) {
                return null; // No content
              }
              return res.ok ? res.json() : null;
            }).catch(() => null)
          );
        }

        // Load stats for standard videos
        if (item.video) {
          statsPromises.push(
            fetch(`${API_CONFIG.BASE_URL}/video-analytics/statistics/${encodeURIComponent(item.moduleName)}/${item.id}/standard`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).then(res => res.ok ? res.json() : null).catch(() => null)
          );
          feedbackPromises.push(
            fetch(`${API_CONFIG.BASE_URL}/video-analytics/feedback/${encodeURIComponent(item.moduleName)}/${item.id}/standard`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).then(res => {
              if (res.ok && res.status === 204) {
                return null; // No content
              }
              return res.ok ? res.json() : null;
            }).catch(() => null)
          );
        }
      });

      const statsResults = await Promise.all(statsPromises);
      const feedbackResults = await Promise.all(feedbackPromises);

      const newStats = {};
      const newFeedback = {};
      const newQuickFeedback = {};
      let statsIndex = 0;
      let feedbackIndex = 0;

      basissicherheitCategory.items.forEach(item => {
        if (getItemVideo(item)) {
          const videoKey = `${item.moduleName}-${item.id}-microlearning`;
          if (statsResults[statsIndex]) {
            newStats[videoKey] = statsResults[statsIndex];
          }
          if (feedbackResults[feedbackIndex]) {
            newFeedback[videoKey] = feedbackResults[feedbackIndex];
            // Set quick feedback based on rating: 4 = thumbs_up, 1 = thumbs_down
            if (feedbackResults[feedbackIndex].rating === 4) {
              newQuickFeedback[videoKey] = 'thumbs_up';
            } else if (feedbackResults[feedbackIndex].rating === 1) {
              newQuickFeedback[videoKey] = 'thumbs_down';
            }
          }
          statsIndex++;
          feedbackIndex++;
        }
        if (item.video) {
          const videoKey = `${item.moduleName}-${item.id}-standard`;
          if (statsResults[statsIndex]) {
            newStats[videoKey] = statsResults[statsIndex];
          }
          if (feedbackResults[feedbackIndex]) {
            newFeedback[videoKey] = feedbackResults[feedbackIndex];
            // Set quick feedback based on rating: 4 = thumbs_up, 1 = thumbs_down
            if (feedbackResults[feedbackIndex].rating === 4) {
              newQuickFeedback[videoKey] = 'thumbs_up';
            } else if (feedbackResults[feedbackIndex].rating === 1) {
              newQuickFeedback[videoKey] = 'thumbs_down';
            }
          }
          statsIndex++;
          feedbackIndex++;
        }
      });

      setVideoStats(newStats);
      setUserFeedback(newFeedback);
      setQuickFeedback(newQuickFeedback);
    };

    loadVideoData();
  }, [checklistItems, user, milieuName]);

  // Track video view when video starts playing
  const handleVideoPlay = async (item, videoType) => {
    if (!user || !item.moduleName) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Get milieu code from quiz result
    const milieuCode = milieuName ? getMilieuAbbreviation(milieuName) : null;

    try {
      await fetch(`${API_CONFIG.BASE_URL}/video-analytics/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module_name: item.moduleName,
          item_id: item.id,
          video_type: videoType,
          milieu_code: milieuCode,
        }),
      });

      // Update local stats
      const videoKey = `${item.moduleName}-${item.id}-${videoType}`;
      setVideoStats(prev => ({
        ...prev,
        [videoKey]: {
          ...prev[videoKey],
          total_views: (prev[videoKey]?.total_views || 0) + 1,
        },
      }));
    } catch (error) {
      console.error('Error recording video view:', error);
    }
  };

  // Quick feedback (thumbs up/down)
  const handleQuickFeedback = async (item, videoType, feedbackType) => {
    if (!user || !item.moduleName) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Sie müssen angemeldet sein, um Feedback zu geben.');
      return;
    }

    // Get milieu code from quiz result
    const milieuCode = milieuName ? getMilieuAbbreviation(milieuName) : null;

    // Convert thumbs up/down to rating: thumbs_up = 4, thumbs_down = 1
    const rating = feedbackType === 'thumbs_up' ? 4 : 1;

    const videoKey = `${item.moduleName}-${item.id}-${videoType}`;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/video-analytics/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module_name: item.moduleName,
          item_id: item.id,
          video_type: videoType,
          milieu_code: milieuCode,
          rating: rating,
          comment: null,
        }),
      });

      if (response.ok) {
        const feedback = await response.json();
        setUserFeedback(prev => ({
          ...prev,
          [videoKey]: feedback,
        }));
        setQuickFeedback(prev => ({
          ...prev,
          [videoKey]: feedbackType,
        }));

        // Update stats asynchronously in the background (don't block UI)
        fetch(`${API_CONFIG.BASE_URL}/video-analytics/statistics/${encodeURIComponent(item.moduleName)}/${item.id}/${videoType}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(statsResponse => {
            if (statsResponse.ok) {
              return statsResponse.json();
            }
          })
          .then(stats => {
            if (stats) {
              setVideoStats(prev => ({
                ...prev,
                [videoKey]: stats,
              }));
            }
          })
          .catch(error => {
            console.error('Error updating video stats:', error);
            // Don't show error to user, stats update is not critical
          });
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to submit quick feedback:', errorData);
        alert(`Fehler beim Absenden des Feedbacks: ${errorData.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Error submitting quick feedback:', error);
      alert('Fehler beim Absenden des Feedbacks. Bitte versuchen Sie es erneut.');
    }
  };

  // Open feedback dialog
  const handleOpenFeedback = (item, videoType) => {
    // Store item and videoType separately instead of using split
    setFeedbackVideoKey(JSON.stringify({ moduleName: item.moduleName, itemId: item.id, videoType }));
    const videoKey = `${item.moduleName}-${item.id}-${videoType}`;
    const existingFeedback = userFeedback[videoKey];
    setFeedbackRating(existingFeedback?.rating || 0);
    setFeedbackComment(existingFeedback?.comment || '');
    setFeedbackCommentError('');
    setFeedbackDialogOpen(true);
  };

  // Submit feedback
  const handleSubmitFeedback = async () => {
    if (!feedbackVideoKey || !user || feedbackRating === 0 || feedbackSubmitting) return;

    // Validate comment before submission
    const commentError = validateComment(feedbackComment);
    if (commentError) {
      setFeedbackCommentError(commentError);
      return;
    }

    let moduleName, itemId, videoType;
    try {
      // Try to parse as JSON first (new format)
      const parsed = JSON.parse(feedbackVideoKey);
      moduleName = parsed.moduleName;
      itemId = parsed.itemId;
      videoType = parsed.videoType;
    } catch (e) {
      // Fallback to old split format for backwards compatibility
      const parts = feedbackVideoKey.split('-');
      if (parts.length >= 3) {
        // Handle module names with spaces - take everything except last 2 parts
        moduleName = parts.slice(0, -2).join('-');
        itemId = parts[parts.length - 2];
        videoType = parts[parts.length - 1];
      } else {
        console.error('Invalid feedbackVideoKey format:', feedbackVideoKey);
        alert('Fehler: Ungültiges Video-Format');
        return;
      }
    }

    const itemIdNum = parseInt(itemId);
    if (isNaN(itemIdNum)) {
      console.error('Invalid itemId:', itemId);
      alert('Fehler: Ungültige Item-ID');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Sie müssen angemeldet sein, um Feedback zu geben.');
      return;
    }

    // Get milieu code from quiz result
    const milieuCode = milieuName ? getMilieuAbbreviation(milieuName) : null;

    setFeedbackSubmitting(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/video-analytics/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module_name: moduleName,
          item_id: itemIdNum,
          video_type: videoType,
          milieu_code: milieuCode,
          rating: feedbackRating,
          comment: feedbackComment ? sanitizeComment(feedbackComment) : null,
        }),
      });

      if (response.ok) {
        const feedback = await response.json();
        const videoKey = `${moduleName}-${itemId}-${videoType}`;
        setUserFeedback(prev => ({
          ...prev,
          [videoKey]: feedback,
        }));

        // Close dialog immediately after successful submission
        setFeedbackDialogOpen(false);
        setFeedbackVideoKey(null);
        setFeedbackRating(0);
        setFeedbackComment('');
        setFeedbackCommentError('');
        setFeedbackSubmitting(false);

        // Update stats asynchronously in the background (don't block UI)
        fetch(`${API_CONFIG.BASE_URL}/video-analytics/statistics/${encodeURIComponent(moduleName)}/${itemIdNum}/${videoType}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(statsResponse => {
            if (statsResponse.ok) {
              return statsResponse.json();
            }
          })
          .then(stats => {
            if (stats) {
              setVideoStats(prev => ({
                ...prev,
                [videoKey]: stats,
              }));
            }
          })
          .catch(error => {
            console.error('Error updating video stats:', error);
            // Don't show error to user, stats update is not critical
          });
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to submit feedback:', errorData);
        alert(`Fehler beim Absenden des Feedbacks: ${errorData.detail || 'Unbekannter Fehler'}`);
        setFeedbackSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Fehler beim Absenden des Feedbacks. Bitte versuchen Sie es erneut.');
      setFeedbackSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  const handleItemToggle = (itemId) => {
    // Nur wenn als "erledigt" markiert, kann das Modul abgeschlossen werden
    if (!markedAsDone[itemId]) {
      return; // Verhindere Abschluss, wenn nicht als erledigt markiert
    }
    // Nur Basissicherheit Kategorie (Index 0)
    toggleItem(0, itemId);
  };

  const handleMarkAsDone = (itemId) => {
    // Wenn bereits markiert, kann es wieder auf "nicht erledigt" gesetzt werden
    if (markedAsDone[itemId]) {
      // Direkt umschalten ohne Bestätigung
      const newMarked = { ...markedAsDone };
      delete newMarked[itemId];
      setMarkedAsDone(newMarked);
      
      // Save to localStorage
      if (user) {
        const markedKey = `marked_as_done_${user.id}`;
        localStorage.setItem(markedKey, JSON.stringify(newMarked));
        // Also save to database
        saveChecklistProgressWithMarkedAsDone(newMarked);
      }
      
      // Wenn das Modul abgeschlossen war, auch das zurücksetzen
      const item = checklistItems[0]?.items.find(i => i.id === itemId);
      if (item && item.completed) {
        handleItemToggle(itemId);
      }
      return;
    }
    
    // Beim ersten Klick: Bestätigungsdialog öffnen
    setPendingItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmMarkAsDone = () => {
    if (pendingItemId !== null) {
      const newMarked = { ...markedAsDone, [pendingItemId]: true };
      setMarkedAsDone(newMarked);
      
      // Save to localStorage
      if (user) {
        const markedKey = `marked_as_done_${user.id}`;
        localStorage.setItem(markedKey, JSON.stringify(newMarked));
        // Also save to database
        saveChecklistProgressWithMarkedAsDone(newMarked);
      }
      
      setConfirmDialogOpen(false);
      setPendingItemId(null);
    }
  };

  const handleCancelMarkAsDone = () => {
    setConfirmDialogOpen(false);
    setPendingItemId(null);
  };

  const handleCategoryToggle = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  const handleVideoToggle = (categoryName, itemId) => {
    const videoKey = `${categoryName}-${itemId}`;
    setExpandedVideos(prev => ({
      ...prev,
      [videoKey]: !prev[videoKey],
    }));
  };

  const handleChecklistToggle = (itemId) => {
    setExpandedChecklists(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Function to get the checklist for an item based on milieu
  const getItemChecklist = (item) => {
    if (milieuName && item.moduleName) {
      const milieuChecklist = getModuleChecklist(item.moduleName, milieuName);
      if (milieuChecklist) {
        return milieuChecklist;
      }
    }
    // Fallback to default checklist
    if (item.moduleName) {
      return getDefaultChecklist(item.moduleName);
    }
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Unverzichtbar': return 'error';
      case 'Empfohlen': return 'warning';
      case 'Professionell': return 'success';
      // Fallback für alte Werte
      case 'sicherheitsgrad-1': return 'error';
      case 'sicherheitsgrad-2': return 'warning';
      case 'sicherheitsgrad-3': return 'success';
      default: return 'error'; // Default zu 'error' für Unverzichtbar
    }
  };

  // Function to highlight search terms in text
  const highlightSearchTerms = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
    let highlightedText = text;
    
    searchWords.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark style="background-color: #ff9800; color: #000000; padding: 1px 2px; border-radius: 2px; font-weight: 600;">$1</mark>');
    });
    
    return highlightedText;
  };


  // Enhanced search function that searches across Basissicherheit items
  const getSearchResults = () => {
    // Filter to only Basissicherheit category
    const basissicherheitCategory = checklistItems.find(cat => cat.category === 'Basissicherheit');
    if (!basissicherheitCategory) {
      return [];
    }

    // Ensure only valid module IDs are included (IDs: 1, 2, 3, 4, 7, 11, 13, 15, 16, 18)
    const validModuleIds = [1, 2, 3, 4, 7, 11, 13, 15, 16, 18];
    const validItems = basissicherheitCategory.items.filter(item => validModuleIds.includes(item.id));

    if (!searchTerm.trim()) {
      // No search term, return all valid Basissicherheit items
      return [{
        ...basissicherheitCategory,
        items: validItems
      }];
    }

    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
    const matchingItems = validItems.filter(item => {
      const description = milieuName && item.moduleName 
        ? (getModuleDescription(item.moduleName, milieuName) || item.description)
        : item.description;
      const searchText = `${item.title} ${description}`.toLowerCase();
      return searchWords.every(word => searchText.includes(word));
    });

    if (matchingItems.length > 0) {
      return [{
        ...basissicherheitCategory,
        items: matchingItems
      }];
    }

    return [];
  };

  // Nur Basissicherheit Module anzeigen
  const filteredData = getSearchResults();

  if (!isLoaded) {
    return (
      <Box sx={{ 
        background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
        minHeight: '100vh', 
        color: '#ffffff',
      }}>
        <Sidebar 
          open={sidebarOpen} 
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          mobileOpen={mobileSidebarOpen}
          onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />
        <Box sx={{ 
          display: 'flex',
          ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
          pt: { xs: 2, sm: 3, md: 4 },
          pb: 4,
        }}>
          <Container maxWidth="lg" sx={{ flexGrow: 1, width: '100%' }}>
            {/* Zurück zum Lagebild Link */}
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{
                  color: '#42a5f5',
                  '&:hover': {
                    backgroundColor: 'rgba(66, 165, 245, 0.1)',
                  },
                }}
              >
                Zurück zum Lagebild
              </Button>
            </Box>
            
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#ffffff' }}>
              Sicherheits-Checkliste
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
              Lade Checkliste...
            </Typography>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
      minHeight: '100vh', 
      color: '#ffffff',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }
    }}>
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      
      <Box sx={{ 
        display: 'flex',
        ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
        transition: (theme) => theme.transitions.create('margin-left', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        pt: { xs: 2, sm: 3, md: 4 },
        pb: 4,
      }}>
        <Container maxWidth="lg" sx={{ flexGrow: 1, width: '100%' }}>
      {/* Zurück zum Lagebild Link */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{
            color: '#42a5f5',
            '&:hover': {
              backgroundColor: 'rgba(66, 165, 245, 0.1)',
            },
          }}
        >
          Zurück zum Lagebild
        </Button>
      </Box>
      
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/checklist')}
        sx={{ 
          mb: 2,
          color: '#ffffff',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
        variant="outlined"
      >
        Zurück zum Dashboard
      </Button>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#ffffff' }}>
          🔒 Basissicherheit Lernpfad
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 2, color: '#b0b0b0' }}>
          Arbeiten Sie die 10 Basissicherheit-Module ab, um Ihre digitale Sicherheit zu verbessern
        </Typography>
        
        {/* Quick Stats */}
        {checklistItems.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip 
              label={`${checklistItems[0]?.items.length || 0} Module`}
              variant="outlined"
              sx={{
                borderColor: 'rgba(33, 150, 243, 0.5)',
                color: '#42a5f5'
              }}
            />
            <Chip 
              label={`${checklistItems[0]?.items.filter(item => item.completed).length || 0} Abgeschlossen`}
              variant="outlined"
              sx={{
                borderColor: 'rgba(76, 175, 80, 0.5)',
                color: '#4caf50'
              }}
            />
            {checklistItems[0] && (
              <Chip 
                label={`${Math.round((checklistItems[0].items.filter(item => item.completed).length / checklistItems[0].items.length) * 100)}% Fortschritt`}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(33, 150, 243, 0.5)',
                  color: '#42a5f5'
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Search Control */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        borderRadius: 3 
      }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Module durchsuchen (z.B. 'Passwort', 'MFA', 'Backup')..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#42a5f5' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#42a5f5',
                },
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              },
            }}
          />
          {searchTerm && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#b0b0b0' }}>
              💡 Tipp: Verwenden Sie mehrere Wörter für präzisere Ergebnisse
            </Typography>
          )}
        </CardContent>
      </Card>


      {/* Checklist Items - nur Basissicherheit */}
      {filteredData.length > 0 ? (
        filteredData
          .filter(category => category.category === 'Basissicherheit')
          .map((category, categoryIndex) => (
        <Accordion
          key={category.category}
          expanded={expandedCategories[category.category] || false}
          onChange={() => handleCategoryToggle(category.category)}
          sx={{ 
            mb: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: '0 12px 40px rgba(33, 150, 243, 0.3)',
              border: '1px solid rgba(66, 165, 245, 0.3)',
            },
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              borderRadius: 3,
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}
            sx={{
              backgroundColor: category.items.filter(item => item.completed).length === category.items.length 
                ? 'rgba(76, 175, 80, 0.2)' 
                : category.items.filter(item => item.completed).length > 0 
                  ? 'rgba(255, 152, 0, 0.2)' 
                  : 'rgba(255, 255, 255, 0.05)',
              borderRadius: 3,
              '&:hover': {
                backgroundColor: category.items.filter(item => item.completed).length === category.items.length 
                  ? 'rgba(76, 175, 80, 0.3)' 
                  : category.items.filter(item => item.completed).length > 0 
                    ? 'rgba(255, 152, 0, 0.3)' 
                    : 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-expanded': {
                borderRadius: '12px 12px 0 0',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: '#ffffff' }}>
                {category.category}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${category.items.filter(item => item.completed).length}/${category.items.length}`}
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    backgroundColor: category.items.filter(item => item.completed).length === category.items.length 
                      ? 'rgba(76, 175, 80, 0.3)' 
                      : 'rgba(33, 150, 243, 0.3)',
                    color: '#ffffff',
                    border: `1px solid ${category.items.filter(item => item.completed).length === category.items.length 
                      ? 'rgba(76, 175, 80, 0.5)' 
                      : 'rgba(33, 150, 243, 0.5)'}`
                  }}
                />
                {category.items.filter(item => item.completed).length === category.items.length && (
                  <Chip
                    label="✅ Abgeschlossen"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(76, 175, 80, 0.5)',
                      color: '#4caf50'
                    }}
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ borderRadius: '0 0 12px 12px', backgroundColor: 'transparent' }}>
            {/* Category Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                  Fortschritt
                </Typography>
                <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                  {Math.round((category.items.filter(item => item.completed).length / category.items.length) * 100)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 6,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${(category.items.filter(item => item.completed).length / category.items.length) * 100}%`,
                    height: '100%',
                    backgroundColor: category.items.filter(item => item.completed).length === category.items.length ? '#4caf50' : '#2196f3',
                    transition: 'width 0.3s ease-in-out',
                  }}
                />
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              {category.items.map((item) => (
                <Grid item xs={12} key={item.id}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      cursor: 'pointer',
                      border: item.completed ? '2px solid #4caf50' : '1px solid rgba(255, 255, 255, 0.1)',
                      background: item.completed 
                        ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(15, 15, 15, 0.9) 100%)'
                        : 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
                      borderRadius: 3,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: item.completed ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        border: item.completed ? '2px solid #4caf50' : '1px solid rgba(66, 165, 245, 0.3)',
                      }
                    }}
                    onClick={() => handleItemToggle(item.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Checkbox
                            checked={item.completed}
                            onChange={() => handleItemToggle(item.id)}
                            disabled={!markedAsDone[item.id]}
                            sx={{ 
                              mt: 0.5,
                              color: '#ffffff',
                              '&.Mui-checked': {
                                color: '#4caf50',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.3)',
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {!markedAsDone[item.id] && !item.completed && (
                            <Typography variant="caption" sx={{ 
                              color: '#ff9800', 
                              fontSize: '0.65rem',
                              textAlign: 'center',
                              maxWidth: '60px',
                              lineHeight: 1.2
                            }}>
                              Zuerst als erledigt markieren
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography 
                            variant="h6" 
                            component="div" 
                            gutterBottom
                            sx={{ 
                              color: '#ffffff',
                              fontWeight: item.completed ? 600 : 'normal'
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerms(item.title, searchTerm) 
                            }}
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 2,
                              color: '#b0b0b0'
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerms(getItemDescription(item), searchTerm) 
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label={`Sicherheitsgrad: ${item.priority === 'Unverzichtbar' || item.priority === 'sicherheitsgrad-1' ? '1 - Unverzichtbar' : item.priority === 'Empfohlen' || item.priority === 'sicherheitsgrad-2' ? '2 - Empfohlen' : item.priority === 'Professionell' || item.priority === 'sicherheitsgrad-3' ? '3 - Professionell' : item.priority}`}
                              size="small"
                              sx={{
                                backgroundColor: getPriorityColor(item.priority) === 'error' 
                                  ? 'rgba(244, 67, 54, 0.2)' 
                                  : getPriorityColor(item.priority) === 'warning'
                                    ? 'rgba(255, 152, 0, 0.2)'
                                    : 'rgba(76, 175, 80, 0.2)',
                                color: getPriorityColor(item.priority) === 'error' 
                                  ? '#f44336' 
                                  : getPriorityColor(item.priority) === 'warning'
                                    ? '#ff9800'
                                    : '#4caf50',
                                border: `1px solid ${getPriorityColor(item.priority) === 'error' 
                                  ? 'rgba(244, 67, 54, 0.5)' 
                                  : getPriorityColor(item.priority) === 'warning'
                                    ? 'rgba(255, 152, 0, 0.5)'
                                    : 'rgba(76, 175, 80, 0.5)'}`
                              }}
                            />
                            {item.video && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VideoLibraryIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Video button clicked for:', item.title, 'Video path:', item.video);
                                  handleVideoToggle('Basissicherheit', item.id);
                                }}
                                sx={{ 
                                  ml: 1,
                                  borderColor: 'rgba(33, 150, 243, 0.5)',
                                  color: '#42a5f5',
                                  '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                    borderColor: 'rgba(33, 150, 243, 0.7)',
                                  }
                                }}
                              >
                                {expandedVideos[`${category.category}-${item.id}`] ? 'Video ausblenden' : 'Video anzeigen'}
                              </Button>
                            )}
                            {getItemVideo(item) && (
                              <Chip
                                icon={<PlayArrowIcon />}
                                label={expandedMicrolearnings[item.id] ? 'Video ausblenden' : 'Erklärvideo'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMicrolearningToggle(item.id);
                                }}
                                size="small"
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor: expandedMicrolearnings[item.id] 
                                    ? 'rgba(156, 39, 176, 0.3)' 
                                    : 'rgba(156, 39, 176, 0.2)',
                                  color: '#ba68c8',
                                  border: '1px solid rgba(156, 39, 176, 0.5)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(156, 39, 176, 0.3)',
                                    borderColor: 'rgba(156, 39, 176, 0.7)',
                                  }
                                }}
                              />
                            )}
                            {getItemChecklist(item) && (
                              <Chip
                                icon={<ChecklistIcon />}
                                label={expandedChecklists[item.id] ? 'Checkliste ausblenden' : 'Checkliste anzeigen'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChecklistToggle(item.id);
                                }}
                                size="small"
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor: expandedChecklists[item.id] 
                                    ? 'rgba(33, 150, 243, 0.3)' 
                                    : 'rgba(33, 150, 243, 0.2)',
                                  color: '#42a5f5',
                                  border: '1px solid rgba(33, 150, 243, 0.5)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                    borderColor: 'rgba(33, 150, 243, 0.7)',
                                  }
                                }}
                              />
                            )}
                            {item.moduleName && (
                              <Chip
                                icon={<AutoAwesomeIcon />}
                                label="Wie mache ich das?"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/module-guide/${encodeURIComponent(item.moduleName)}`);
                                }}
                                size="small"
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                  color: '#ffc107',
                                  border: '1px solid rgba(255, 193, 7, 0.5)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 193, 7, 0.3)',
                                    borderColor: 'rgba(255, 193, 7, 0.7)',
                                  }
                                }}
                              />
                            )}
                            <Chip
                              icon={markedAsDone[item.id] ? <CheckCircleIcon /> : <WarningIcon />}
                              label={markedAsDone[item.id] ? 'Erledigt (klicken zum Zurücksetzen)' : 'Als erledigt markieren'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsDone(item.id);
                              }}
                              size="small"
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: markedAsDone[item.id] 
                                  ? 'rgba(76, 175, 80, 0.3)' 
                                  : 'rgba(255, 152, 0, 0.2)',
                                color: markedAsDone[item.id] ? '#4caf50' : '#ff9800',
                                border: `1px solid ${markedAsDone[item.id] 
                                  ? 'rgba(76, 175, 80, 0.5)' 
                                  : 'rgba(255, 152, 0, 0.5)'}`,
                                '&:hover': {
                                  backgroundColor: markedAsDone[item.id] 
                                    ? 'rgba(76, 175, 80, 0.4)' 
                                    : 'rgba(255, 152, 0, 0.3)',
                                  borderColor: markedAsDone[item.id] 
                                    ? 'rgba(76, 175, 80, 0.7)' 
                                    : 'rgba(255, 152, 0, 0.7)',
                                }
                              }}
                            />
                          </Box>
                          
                          {/* Microlearning Video Section */}
                          {getItemVideo(item) && (
                            <Collapse in={expandedMicrolearnings[item.id] || false}>
                              <Box sx={{ 
                                mt: 2, 
                                mb: 2,
                                p: 2, 
                                background: 'rgba(156, 39, 176, 0.05)',
                                border: '1px solid rgba(156, 39, 176, 0.2)',
                                borderRadius: 2 
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ba68c8' }}>
                                    <PlayArrowIcon sx={{ color: '#ba68c8' }} />
                                    Erklärvideo zu diesem Modul
                                  </Typography>
                                  {(() => {
                                    const videoKey = `${item.moduleName}-${item.id}-microlearning`;
                                    const stats = videoStats[videoKey];
                                    return stats && (
                                      <Chip
                                        icon={<VisibilityIcon />}
                                        label={`${stats.total_views || 0} Aufrufe`}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                          color: '#ba68c8',
                                          border: '1px solid rgba(156, 39, 176, 0.5)',
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                                <Box sx={{ 
                                  position: 'relative',
                                  width: '100%',
                                  maxWidth: '400px',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                                }}>
                                  <video
                                    controls
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      display: 'block'
                                    }}
                                    preload="metadata"
                                    onPlay={() => handleVideoPlay(item, 'microlearning')}
                                    onError={(e) => {
                                      console.error('Video loading error:', e);
                                      console.error('Video source:', getItemVideo(item));
                                    }}
                                    onLoadStart={() => {
                                      console.log('Microlearning video loading started:', getItemVideo(item));
                                    }}
                                    onCanPlay={() => {
                                      console.log('Microlearning video can play:', getItemVideo(item));
                                    }}
                                  >
                                    <source src={getItemVideo(item)} type="video/mp4" />
                                    Ihr Browser unterstützt das Video-Element nicht.
                                  </video>
                                </Box>
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {(() => {
                                    const videoKey = `${item.moduleName}-${item.id}-microlearning`;
                                    const stats = videoStats[videoKey];
                                    const feedback = userFeedback[videoKey];
                                    const quickFb = quickFeedback[videoKey];
                                    return (
                                      <>
                                        {stats?.average_rating && (
                                          <Chip
                                            icon={<StarIcon />}
                                            label={`${stats.average_rating.toFixed(1)} ⭐ (${stats.total_feedback || 0})`}
                                            size="small"
                                            sx={{
                                              backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                              color: '#ffc107',
                                              border: '1px solid rgba(255, 193, 7, 0.5)',
                                            }}
                                          />
                                        )}
                                        {quickFb !== 'thumbs_up' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleQuickFeedback(item, 'microlearning', 'thumbs_up')}
                                            sx={{
                                              color: '#4caf50',
                                              '&:hover': {
                                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                              }
                                            }}
                                          >
                                            <ThumbUpIcon />
                                          </IconButton>
                                        )}
                                        {quickFb !== 'thumbs_down' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleQuickFeedback(item, 'microlearning', 'thumbs_down')}
                                            sx={{
                                              color: '#f44336',
                                              '&:hover': {
                                                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                              }
                                            }}
                                          >
                                            <ThumbDownIcon />
                                          </IconButton>
                                        )}
                                        <Button
                                          size="small"
                                          startIcon={<ThumbUpIcon />}
                                          onClick={() => handleOpenFeedback(item, 'microlearning')}
                                          sx={{
                                            color: feedback ? '#4caf50' : '#ba68c8',
                                            borderColor: feedback ? 'rgba(76, 175, 80, 0.5)' : 'rgba(156, 39, 176, 0.5)',
                                            '&:hover': {
                                              borderColor: feedback ? 'rgba(76, 175, 80, 0.7)' : 'rgba(156, 39, 176, 0.7)',
                                              backgroundColor: feedback ? 'rgba(76, 175, 80, 0.1)' : 'rgba(156, 39, 176, 0.1)',
                                            }
                                          }}
                                          variant="outlined"
                                        >
                                          {feedback ? 'Feedback bearbeiten' : 'Feedback geben'}
                                        </Button>
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            </Collapse>
                          )}
                          
                          {/* Checklist Section */}
                          {getItemChecklist(item) && (
                            <Collapse in={expandedChecklists[item.id] || false}>
                              <Box sx={{ mt: 2, mb: 2 }}>
                                <Card 
                                  elevation={0}
                                  sx={{
                                    background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(15, 15, 15, 0.3) 100%)',
                                    border: '1px solid rgba(33, 150, 243, 0.3)',
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    <Typography 
                                      variant="subtitle2" 
                                      sx={{ 
                                        color: '#42a5f5', 
                                        fontWeight: 600, 
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}
                                    >
                                      <ChecklistIcon />
                                      Checkliste für dieses Modul:
                                    </Typography>
                                    <List dense sx={{ p: 0 }}>
                                      {getItemChecklist(item).map((checklistItem, index) => (
                                        <ListItem 
                                          key={index} 
                                          sx={{ 
                                            py: 0.5,
                                            px: 0,
                                            '&:before': {
                                              content: '"✓"',
                                              color: '#4caf50',
                                              fontWeight: 'bold',
                                              mr: 1.5,
                                              fontSize: '1.2rem'
                                            }
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                  color: '#ffffff',
                                                  lineHeight: 1.6
                                                }}
                                              >
                                                {checklistItem}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </CardContent>
                                </Card>
                              </Box>
                            </Collapse>
                          )}
                          
                          {/* Video Section */}
                          {item.video && (
                            <Collapse in={expandedVideos[`${category.category}-${item.id}`] || false}>
                              <Box sx={{ 
                                mt: 2, 
                                p: 2, 
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 2 
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffffff' }}>
                                    <PlayArrowIcon sx={{ color: '#42a5f5' }} />
                                    Lernvideo zu diesem Thema
                                  </Typography>
                                  {(() => {
                                    const videoKey = `${item.moduleName}-${item.id}-standard`;
                                    const stats = videoStats[videoKey];
                                    return stats && (
                                      <Chip
                                        icon={<VisibilityIcon />}
                                        label={`${stats.total_views || 0} Aufrufe`}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                          color: '#42a5f5',
                                          border: '1px solid rgba(33, 150, 243, 0.5)',
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                                <Box sx={{ 
                                  position: 'relative',
                                  width: '100%',
                                  maxWidth: '400px',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                                }}>
                                  <video
                                    controls
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      display: 'block'
                                    }}
                                    preload="metadata"
                                    onPlay={() => handleVideoPlay(item, 'standard')}
                                    onError={(e) => {
                                      console.error('Video loading error:', e);
                                      console.error('Video source:', item.video);
                                    }}
                                    onLoadStart={() => {
                                      console.log('Video loading started:', item.video);
                                    }}
                                    onCanPlay={() => {
                                      console.log('Video can play:', item.video);
                                    }}
                                  >
                                    <source src={item.video} type="video/mp4" />
                                    Ihr Browser unterstützt das Video-Element nicht.
                                  </video>
                                </Box>
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {(() => {
                                    const videoKey = `${item.moduleName}-${item.id}-standard`;
                                    const stats = videoStats[videoKey];
                                    const feedback = userFeedback[videoKey];
                                    const quickFb = quickFeedback[videoKey];
                                    return (
                                      <>
                                        {stats?.average_rating && (
                                          <Chip
                                            icon={<StarIcon />}
                                            label={`${stats.average_rating.toFixed(1)} ⭐ (${stats.total_feedback || 0})`}
                                            size="small"
                                            sx={{
                                              backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                              color: '#ffc107',
                                              border: '1px solid rgba(255, 193, 7, 0.5)',
                                            }}
                                          />
                                        )}
                                        {quickFb !== 'thumbs_up' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleQuickFeedback(item, 'standard', 'thumbs_up')}
                                            sx={{
                                              color: '#4caf50',
                                              '&:hover': {
                                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                              }
                                            }}
                                          >
                                            <ThumbUpIcon />
                                          </IconButton>
                                        )}
                                        {quickFb !== 'thumbs_down' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleQuickFeedback(item, 'standard', 'thumbs_down')}
                                            sx={{
                                              color: '#f44336',
                                              '&:hover': {
                                                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                              }
                                            }}
                                          >
                                            <ThumbDownIcon />
                                          </IconButton>
                                        )}
                                        <Button
                                          size="small"
                                          startIcon={<ThumbUpIcon />}
                                          onClick={() => handleOpenFeedback(item, 'standard')}
                                          sx={{
                                            color: feedback ? '#4caf50' : '#42a5f5',
                                            borderColor: feedback ? 'rgba(76, 175, 80, 0.5)' : 'rgba(33, 150, 243, 0.5)',
                                            '&:hover': {
                                              borderColor: feedback ? 'rgba(76, 175, 80, 0.7)' : 'rgba(33, 150, 243, 0.7)',
                                              backgroundColor: feedback ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                                            }
                                          }}
                                          variant="outlined"
                                        >
                                          {feedback ? 'Feedback bearbeiten' : 'Feedback geben'}
                                        </Button>
                                      </>
                                    );
                                  })()}
                                </Box>
                                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#b0b0b0' }}>
                                  💡 Tipp: Schauen Sie sich das Video an, um mehr über dieses wichtige Sicherheitsthema zu erfahren.
                                </Typography>
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
        ))
      ) : (
        <Card sx={{ 
          p: 4, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Keine Checklisten gefunden
          </Typography>
        </Card>
      )}
        </Container>
      </Box>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => {
          setFeedbackDialogOpen(false);
          setFeedbackVideoKey(null);
          setFeedbackRating(0);
          setFeedbackComment('');
          setFeedbackCommentError('');
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(26, 26, 26, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            minWidth: '400px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: 2 }}>
          <ThumbUpIcon sx={{ color: '#42a5f5', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
            Video-Feedback
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0' }}>
              Wie hilfreich war dieses Video für Sie?
            </Typography>
            <Rating
              value={feedbackRating}
              onChange={(event, newValue) => {
                setFeedbackRating(newValue || 0);
              }}
              max={4}
              size="large"
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#ffc107',
                },
                '& .MuiRating-iconEmpty': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            <Typography variant="body2" sx={{ mt: 3, mb: 1, color: '#b0b0b0' }}>
              Optional: Kommentar
            </Typography>
            <TextareaAutosize
              minRows={4}
              value={feedbackComment}
              onChange={(e) => {
                const value = e.target.value;
                const sanitized = sanitizeComment(value);
                setFeedbackComment(sanitized);
                const error = validateComment(sanitized);
                setFeedbackCommentError(error);
              }}
              placeholder="Ihre Meinung zum Video... (max. 1000 Zeichen)"
              maxLength={1000}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: feedbackCommentError 
                  ? '1px solid rgba(244, 67, 54, 0.7)' 
                  : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#ffffff',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical',
              }}
            />
            {feedbackCommentError && (
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#f44336' }}>
                {feedbackCommentError}
              </Typography>
            )}
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#b0b0b0' }}>
              {feedbackComment.length}/1000 Zeichen
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setFeedbackDialogOpen(false);
              setFeedbackVideoKey(null);
              setFeedbackRating(0);
              setFeedbackComment('');
              setFeedbackCommentError('');
            }}
            sx={{
              color: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmitFeedback}
            variant="contained"
            disabled={feedbackRating === 0 || feedbackRating > 4 || feedbackSubmitting}
            startIcon={feedbackSubmitting ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <ThumbUpIcon />}
            sx={{
              backgroundColor: '#42a5f5',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            {feedbackSubmitting ? 'Wird gesendet...' : 'Feedback absenden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bestätigungsdialog für "Erledigt" */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelMarkAsDone}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(26, 26, 26, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
            Modul als erledigt markieren
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#b0b0b0', lineHeight: 1.8 }}>
            Möchten Sie dieses Modul wirklich als erledigt markieren?
            <br /><br />
            <strong style={{ color: '#ffffff' }}>Wichtig:</strong> Wir können nicht automatisch prüfen, ob Sie die Maßnahme tatsächlich umgesetzt haben. 
            Bitte markieren Sie das Modul nur als erledigt, wenn Sie die Sicherheitsmaßnahme wirklich durchgeführt haben.
            <br /><br />
            Nach der Bestätigung können Sie das Modul als abgeschlossen markieren.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCancelMarkAsDone}
            sx={{
              color: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmMarkAsDone}
            variant="contained"
            startIcon={<CheckCircleIcon />}
            sx={{
              backgroundColor: '#4caf50',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#45a049',
              }
            }}
          >
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Checklist;

